"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RangeInput } from "@/components/ui/range-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const specs = [{
  label: "EFFICIENCY GAIN",
  value: "UP TO 70%"
}, {
  label: "SYSTEM INTEGRATION",
  value: "FULL STACK"
}, {
  label: "AI MODELS",
  value: "12+ DEPLOYED"
}];
const thumbnails = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_workflow_dashboard.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_workflow_dashboard.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_workflow_dashboard.webp-wvc-sizes",
  alt: "AI workflow dashboard interface"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_server_node.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_server_node.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_server_node.webp-wvc-sizes",
  alt: "Server node for automation"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/digital_strategy_map.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/digital_strategy_map.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/digital_strategy_map.png-wvc-sizes",
  alt: "Digital strategy mapping"
}];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-[color:var(--border)] bg-background/40">
        <p className="font-default text-lg text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-md">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="John Smith" className="rounded-md bg-transparent border-0 border-b border-[color:var(--border)] px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--foreground)] text-foreground placeholder:text-muted-foreground/60" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Business Email
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@company.com" className="rounded-md bg-transparent border-0 border-b border-[color:var(--border)] px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--foreground)] text-foreground placeholder:text-muted-foreground/60" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="0468 075 702" className="rounded-md bg-transparent border-0 border-b border-[color:var(--border)] px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--foreground)] text-foreground placeholder:text-muted-foreground/60" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="event_date" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Project Start Date
            </FormLabel>
            <FormControl>
              <Input type="date" className="rounded-md bg-transparent border-0 border-b border-[color:var(--border)] px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--foreground)] text-foreground [color-scheme:dark]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="attendance" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground flex justify-between">
              <span>Team Size</span>
              <span className="text-[color:var(--foreground)]">{field.value || 10} EMPLOYEES</span>
            </FormLabel>
            <FormControl>
              <RangeInput min={1} max={500} step={5} value={Number(field.value) || 10} onValueChange={val => field.onChange(val)} className="w-full h-1 bg-[color:var(--border)] appearance-none cursor-pointer accent-[color:var(--foreground)]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="event_type" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Service Required
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="rounded-md bg-transparent border-0 border-b border-[color:var(--border)] px-0 h-11 focus-visible:ring-0 focus:border-[color:var(--foreground)] text-foreground">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="rounded-md bg-popover border border-[color:var(--border)]">
                  <SelectItem value="Customer Support">Customer Support</SelectItem>
                  <SelectItem value="Appointment Booking">Appointment Booking</SelectItem>
                  <SelectItem value="Invoicing">Invoicing</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Project Requirements
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your operational bottlenecks and goals..." className="rounded-md bg-transparent border-0 border-b border-[color:var(--border)] px-0 min-h-24 focus-visible:ring-0 focus-visible:border-[color:var(--foreground)] text-foreground placeholder:text-muted-foreground/60 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="agree_terms" render={({
      field
    }) => <FormItem>
            <div className="flex items-start gap-3 pt-2">
              <input type="checkbox" checked={!!field.value} onChange={e => field.onChange(e.target.checked)} className="mt-1 size-4 accent-[color:var(--foreground)] cursor-pointer" id="agree_terms" />
              <label htmlFor="agree_terms" className="font-mono text-xs uppercase tracking-wider text-muted-foreground cursor-pointer">
                I agree to the privacy policy
              </label>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/50 bg-destructive/10">
          <p className="text-destructive text-sm font-mono">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-md bg-[color:var(--foreground)] text-[color:var(--background)] hover:bg-[color:var(--foreground)]/80 font-default font-bold uppercase tracking-widest text-sm shadow-2xl">
        {isSubmitting ? "Processing..." : <>
            Submit Request <ArrowRight className="size-4" />
          </>}
      </Button>

      <div className="flex items-center gap-2 pt-2">
        <span className="size-2 rounded-md bg-[color:var(--foreground)]" />
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          Response Time: Under 24 Hours
        </p>
      </div>
    </Form>;
}
export default function Automation() {
  return <section data-section-id="2637" id="lets-talk-automation" className="bg-background text-foreground py-24 md:py-40 px-6 md:px-10 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div className="mb-16 md:mb-24">
          <span className="font-mono text-xs md:text-sm uppercase tracking-widest text-primary-foreground">
            06 / Contact Us
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-6 flex flex-col gap-10">
            <div>
              <h2 className="font-default font-bold uppercase leading-[0.9] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
                <span className="block text-foreground">Let's /</span>
                <span className="block" style={{
                WebkitTextStroke: "2px var(--foreground)",
                color: "transparent"
              }}>
                  Automate
                </span>
              </h2>
            </div>

            <p className="font-serif text-lg md:text-xl leading-relaxed max-w-xl text-primary-foreground">
              Ready to streamline your operations? Partner with AutolexaAi to integrate intelligent workflows that save time, reduce costs, and scale your business efficiency.
            </p>

            {}
            <div className="border-t border-[color:var(--border)] pt-8">
              <div className="flex flex-col gap-4">
                {specs.map((spec, i) => <div data-index={i} key={i} className="flex items-baseline font-mono text-sm md:text-base uppercase tracking-wider">
                    <span className="text-muted-foreground">{spec.label}</span>
                    <span className="flex-1 mx-3 border-b border-dotted border-[color:var(--border)]" aria-hidden="true" />
                    <span className="text-[color:var(--foreground)] font-medium">{spec.value}</span>
                  </div>)}
              </div>
            </div>

            {}
            <div className="grid grid-cols-3 gap-3 pt-4">
              {thumbnails.map((thumb, i) => <div data-index={i} key={i} className="aspect-square overflow-hidden border border-[color:var(--border)] grayscale">
                  <img src={thumb.src} data-wvc-srcset={thumb.srcset} data-wvc-sizes={thumb.sizes} alt={thumb.alt} loading="lazy" decoding="async" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>)}
            </div>
          </div>

          {}
          <div className="lg:col-span-6">
            <FormProvider formKey="automation_lead_form_connect" formId="automation_lead_form_connect" sectionName="lets-talk-automation" defaultValues={{
            name: "",
            email: "",
            phone: "",
            event_date: "",
            event_type: "",
            attendance: 10,
            details: "",
            agree_terms: false
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            event_date: {
              required: true
            },
            event_type: {
              required: true
            },
            attendance: {
              min: 1,
              max: 500
            },
            agree_terms: {
              required: true
            }
          }} submitText="Submit Request" successMessage="Inquiry received. Our team will contact you within 24 hours to discuss your automation strategy." errorMessage="Something went wrong. Please try again or email us at info@autolexaai.com." className="relative rounded-md border border-[color:var(--border)] bg-card/40 backdrop-blur-xl p-8 md:p-12 shadow-4xl">
              {}
              <div className="mb-6">
                <span className="font-mono text-xs uppercase tracking-widest text-[color:var(--foreground)]">
                  Consultation / 001
                </span>
              </div>

              <h3 className="font-default font-bold uppercase text-2xl md:text-3xl tracking-tight mb-8 text-foreground">
                Start Your Transformation
              </h3>

              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}